import * as Yup from 'yup';

const SUM_INSURED_BLOCKED_MSG =
  'Policy Cover is linked to one or more active Facultative. To update the Sum Insured, the Facultative should be closed.';
const PREMIUM_BLOCKED_MSG =
  'Policy Cover is linked to one or more active Facultative. To update the Premium, the Facultative should be closed.';

const toOptionalNumber = (schema: Yup.NumberSchema<number | undefined>) =>
  schema.transform((val, orig) =>
    orig === '' || orig === null ? undefined : val
  );

const nonNegativeAmount = () =>
  toOptionalNumber(
    Yup.number().typeError('Must be a number').min(0, 'Cannot be negative')
  );

const requiredNonNegativeAmount = () =>
  nonNegativeAmount().required('Required');

const nonNegativeInteger = () =>
  toOptionalNumber(
    Yup.number()
      .typeError('Must be a number')
      .integer('Must be a whole number')
      .min(0, 'Cannot be negative')
  );

export const coversValidationSchema = (
  lineExternalCode?: string,
  rowIsEnabled?: (row: any) => boolean
) => {
  const isLine51 = lineExternalCode === '51';

  const rowSchema = Yup.object({
    policyCover: Yup.string().required('Required'),
    isMain: Yup.boolean().required('Required'),
    isMandatory: Yup.boolean().required('Required'),

     coverEffectiveFrom: Yup.string().notRequired(),
     coverEffectiveTo: Yup.string().notRequired(),

    sumInsuredIsAdditive: Yup.boolean().required('Required'),
    coverSumInsured: requiredNonNegativeAmount()
      .label('Sum Insured')
      .test(
        'active-fac-sum-insured',
        SUM_INSURED_BLOCKED_MSG,
        async function (value) {
          const validateActiveFacField = (this.options as any)?.context
            ?.validateActiveFacField;
          if (typeof validateActiveFacField !== 'function') return true;

          const policyPlanCoverId = (this.parent as any)?.policyPlanCoverId;
          if (!policyPlanCoverId) return true;

          return validateActiveFacField({
            kind: 'SUM_INSURED',
            policyPlanCoverId: String(policyPlanCoverId),
            value
          });
        }
      ),

    coverPremiumType: Yup.string().when([], {
      is: () => isLine51,
      then: (s) => s.required('Required'),
      otherwise: (s) => s.notRequired()
    }),

    coverPremiumAmount: Yup.mixed().when(
      ['coverPremiumType'],
      ([typ], _schema: Yup.Schema<any>) => {
        const t = typeof typ === 'string' ? typ : '';
        if (t === 'AMOUNT') {
          return requiredNonNegativeAmount()
            .label('Premium')
            .test('active-fac-premium', PREMIUM_BLOCKED_MSG, async function (value) {
              const validateActiveFacField = (this.options as any)?.context
                ?.validateActiveFacField;
              if (typeof validateActiveFacField !== 'function') return true;

              const policyPlanCoverId = (this.parent as any)?.policyPlanCoverId;
              if (!policyPlanCoverId) return true;

              return validateActiveFacField({
                kind: 'PREMIUM',
                policyPlanCoverId: String(policyPlanCoverId),
                value
              });
            });
        }

        return toOptionalNumber(Yup.number());
      }
    ),

    excessOnClaimType: Yup.string().required('Required'),

    excessOnClaimAmount: Yup.string().when('excessOnClaimType', {
      is: 'AMOUNT',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired()
    }),

    excessOnClaimDays: Yup.string().when('excessOnClaimType', {
      is: 'DAYS',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired()
    }),

    excessOnClaimPercentage: Yup.string().when('excessOnClaimType', {
      is: 'PERCENTAGE',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired()
    })
  }).test('enabled-skip', 'ok', function (row) {
    if (rowIsEnabled && !rowIsEnabled(row)) return true;

    return true;
  });

  return Yup.object({
    covers: Yup.array().of(rowSchema)
  });
};
